import { type ReactNode } from "react";
import { useInspectorFlyoutWidth, type InspectorFlyoutWidthConfig } from "./useInspectorFlyoutWidth.js";

export type { InspectorFlyoutWidthConfig };

export type InspectorFlyoutProps = {
  /** Surface identity, used to scope persisted width (e.g. "operationEditor"). */
  surfaceId: string;
  /** Panel identity within the surface (e.g. "inspector", "ai", "saved"). */
  panelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Vertical strip label shown when collapsed. */
  stripLabel: string;
  /** 0-indexed strip offset so multiple panels on one surface don't overlap. */
  stripSlot?: number;
  /**
   * When closed, suppress the vertical strip entirely. Used on multi-panel
   * surfaces so a closed panel's strip doesn't sit next to an open sibling.
   */
  hideStrip?: boolean;
  /** Top of the panel/strip in px (surface topbar offset). */
  topOffset: number;
  widthConfig: InspectorFlyoutWidthConfig;
  /** Identity / navigation region at the header's left (node dropdown or a static title). */
  headerNav: ReactNode;
  /** Panel-specific header buttons (Duplicate, Reset, Extract, …). */
  headerActions?: ReactNode;
  /** Body content — typically an <InspectorTabs>, or raw content for untabbed panels. */
  children: ReactNode;
};

const STRIP_HEIGHT = 110;
const STRIP_GAP = 8;

/**
 * Right-edge overlay inspector panel (ADR-0129 §3.1). Fixed-position, no
 * backdrop — the uncovered surface stays interactive. Collapses to a vertical
 * strip; drag-resizes; width persists per surface + panel. One implementation
 * replacing the three legacy rails.
 */
export function InspectorFlyout({
  surfaceId,
  panelId,
  open,
  onOpenChange,
  stripLabel,
  stripSlot = 0,
  hideStrip = false,
  topOffset,
  widthConfig,
  headerNav,
  headerActions,
  children,
}: InspectorFlyoutProps) {
  const { width, isResizing, startResize } = useInspectorFlyoutWidth({
    surfaceId,
    panelId,
    min: widthConfig.min,
    max: widthConfig.max,
    default: widthConfig.default,
  });

  if (!open) {
    if (hideStrip) return null;
    const stripTop = topOffset + stripSlot * (STRIP_HEIGHT + STRIP_GAP);
    return (
      <button
        type="button"
        className="inspectorFlyoutStrip"
        style={{ top: stripTop }}
        title={`Open ${stripLabel}`}
        onClick={() => onOpenChange(true)}
      >
        <span className="inspectorFlyoutStripLabel">{stripLabel}</span>
      </button>
    );
  }

  return (
    <div
      className="inspectorFlyout"
      style={{ top: topOffset, width, transition: isResizing ? "none" : "width 0.2s" }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        className="inspectorFlyoutResizeHandle"
        onPointerDown={startResize}
      />
      <div className="inspectorFlyoutHeader">
        <div className="inspectorFlyoutHeaderNav">{headerNav}</div>
        {headerActions ? <div className="inspectorFlyoutHeaderActions">{headerActions}</div> : null}
        <button
          type="button"
          className="btn btnSm inspectorFlyoutClose"
          aria-label={`Close ${stripLabel}`}
          title="Close panel"
          onClick={() => onOpenChange(false)}
        >
          ▶
        </button>
      </div>
      <div className="inspectorFlyoutBody">{children}</div>
    </div>
  );
}
